import React, { useState } from 'react';
import Modal from '../shared/Modal';
import { Checkbox, FormControl, FormControlLabel, FormGroup, makeStyles, Radio } from '@material-ui/core';

import Avatar from '../shared/members/Avatar';
import { SettingsButtons } from '../shared/Settings';
import ReactLoading from 'react-loading';
import { useGetCurrentUserQuery, useGetOfficesQuery, useUserSearchQuery } from 'utils/wemble-api';
import { UserDto, UserSearchParams } from 'utils/wemble-api.generated';
import TeamSelect from '../shared/TeamSelect';
import { compareUsersByName } from 'helpers/compareFunctions';

const useStyles = makeStyles((theme) => ({
  select: {
    fontFamily: 'Roboto',
  },
  select__title: {
    color: theme.palette.gray.g1,
    '& h1': {
      fontSize: 28,
      fontWeight: 600,
    },
    '& p': {
      color: theme.palette.gray.g8,
    },
  },
  select__filters: {
    paddingBlock: 10,
    gap: 8,
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  select__list: {
    maxHeight: 400,
    overflowY: 'scroll',
  },
  listItem: {
    borderBottom: 'solid 1px',
    borderBottomColor: theme.palette.gray.g7,
    marginBlock: 0,
    userSelect: 'none',
  },
  label: {
    width: '100%',
  },
  checkBox: {
    color: theme.palette.gray.g1,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '&.MuiCheckbox-colorPrimary.Mui-checked': {
      color: theme.palette.gray.g1,
    },
  },
  list__user: {
    display: 'flex',
    alignItems: 'center',
    paddingBlock: 8,
    margin: 0,
  },
  user__project: {
    textAlign: 'right',
    flexGrow: 1,
    '& h2': {
      fontSize: 14,
      fontWeight: 400,
      margin: 0,
      color: theme.palette.gray.g8,
    },
    '& p': {
      fontSize: 15,
      fontWeight: 500,
      margin: 0,
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  loader: {
    fill: '#59abcb',
    margin: 'auto',
    marginTop: '50px',
    marginBottom: '50px',
    width: '20%',
  },
  filterSelect: {
    fontSize: 13,
    padding: '10px 30px 10px 14px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#f5f5f5',
    height: 44,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none' as any,
    WebkitAppearance: 'none' as any,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23999' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    minWidth: 120,
    width: '100%',
    transition: 'transform 0.15s, opacity 0.15s',
    '&:hover': {
      transform: 'scale(0.97)',
      opacity: 0.8,
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
    },
  },
}));

const UserPicker = ({
  type,
  description,
  visible,
  onClose,
  onSelect,
  userFilter,
}: {
  type: 'competence' | 'manager' | 'project';
  description?: string;
  visible: boolean;
  onClose: () => void;
  onSelect: (selectedUsers: UserDto[]) => void;
  userFilter?: (user: UserDto) => boolean;
}) => {
  const classes = useStyles();
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: offices } = useGetOfficesQuery();

  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const initializedRef = React.useRef(false);

  // Default to current user's team
  React.useEffect(() => {
    if (currentUser && offices && !initializedRef.current) {
      initializedRef.current = true;
      const officeId = typeof currentUser.office === 'string' ? currentUser.office : currentUser.office?._id;
      setSelectedTeams(officeId ? [officeId] : ['__all__']);
    }
  }, [currentUser, offices]);

  const searchParams: UserSearchParams = React.useMemo(() => {
    if (!selectedTeams.length || selectedTeams[0] === '__all__') return {};
    return { teams: selectedTeams } as any;
  }, [selectedTeams]);

  const { data: searchResult, isFetching } = useUserSearchQuery({ ...searchParams, includeAdmins: true }, { skip: !visible || !initializedRef.current, refetchOnMountOrArgChange: 15, refetchOnReconnect: true });

  const users = searchResult?.users?.filter(u => {
    if (userFilter && !userFilter(u as UserDto)) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term);
    }
    return true;
  }).sort(compareUsersByName) ?? [];

  const handleUserSelect = (selectedUser: UserDto, checked: boolean) => {
    if (type === 'manager') {
      setSelectedUsers([selectedUser]);
    } else {
      const updated = selectedUsers.filter((user) => user._id !== selectedUser._id);
      if (checked) {
        updated.push(selectedUser);
      }
      setSelectedUsers(updated);
    }
  };

  return (
    <Modal visible={visible} close={onClose} customWidth={600}>
      <div className={classes.select}>
        <div className={classes.select__title}>
          <h1>Search users</h1>
          <p>{description ? description : `Add users to ${type}`}</p>
        </div>
        <div className={classes.select__filters} style={{ display: 'flex' }}>
          <input
            autoFocus
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              minWidth: 150,
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid #e0e0e0',
              fontSize: 13,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <TeamSelect
            teams={(searchResult?.facets as any)?.teams}
            value={selectedTeams}
            onChange={setSelectedTeams}
          />
        </div>
      </div>

      <div className={classes.select__list}>
        <FormControl component="fieldset" fullWidth={true}>
          <FormGroup aria-label="position">
            {!isFetching &&
              users.map((user) => (
                <FormControlLabel
                  className={classes.listItem}
                  key={user._id}
                  value="end"
                  classes={{ label: classes.label }}
                  control={
                    type === 'manager' ? (
                      <Radio
                        color="primary"
                        className={classes.checkBox}
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        checked={selectedUsers.some((u) => u._id === user._id)}
                        onChange={(event) => handleUserSelect(user as UserDto, event.target.checked)}
                      />
                    ) : (
                      <Checkbox
                        color="primary"
                        className={classes.checkBox}
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onChange={(event) => handleUserSelect(user as UserDto, event.target.checked)}
                      />
                    )
                  }
                  label={
                    <div className={classes.list__user}>
                      <Avatar
                        image={user.profilePicture === undefined ? '' : user.profilePicture}
                        name={user.name}
                        email={`${(offices?.find((office) => office._id === user.office) || { name: '' })?.name}`}
                      />
                      {!!user?.projects?.length && type === 'project' && (
                        <div className={classes.user__project}>
                          <h2>#Of Projects</h2>
                          <p>{user.projects.length}</p>
                        </div>
                      )}
                    </div>
                  }
                  labelPlacement="end"
                />
              ))}
            {isFetching && <ReactLoading type={'spinningBubbles'} className={classes.loader} />}
          </FormGroup>
        </FormControl>
      </div>
      <SettingsButtons
        discardCustom={'Cancel'}
        discard={onClose}
        saveCustom={'Add Selected Users'}
        save={() => {
          onClose();
          onSelect(selectedUsers);
          setSelectedUsers([]);
        }}
        customPadding={15}
      />
    </Modal>
  );
};

export default UserPicker;
